import { useEffect, useState } from 'react';

/**
 * The workspace, once revealed for a proposal, stays revealed. A mutation that
 * momentarily empties a field must not yank the document away, so the reveal is
 * latched in localStorage per proposal id.
 */
const key = (proposalId: string): string => `visitiq.workspace.emerged.${proposalId}`;

function read(proposalId: string): boolean {
  try {
    return window.localStorage.getItem(key(proposalId)) === '1';
  } catch {
    return false;
  }
}

export interface Emergence {
  /** True once the workspace has ever been revealed for this proposal. */
  emerged: boolean;
  /** True only on the render where it first flipped — drives the animation. */
  justEmerged: boolean;
}

export function useEmergence(proposalId: string | null, conditionMet: boolean): Emergence {
  const [emerged, setEmerged] = useState<boolean>(() =>
    proposalId ? read(proposalId) : false,
  );
  const [justEmerged, setJustEmerged] = useState(false);

  useEffect(() => {
    setEmerged(proposalId ? read(proposalId) : false);
    setJustEmerged(false);
  }, [proposalId]);

  useEffect(() => {
    if (!proposalId || !conditionMet || emerged) return;
    try {
      window.localStorage.setItem(key(proposalId), '1');
    } catch {
      /* private mode — the latch is a nicety, not a requirement */
    }
    setEmerged(true);
    setJustEmerged(true);
    const timer = window.setTimeout(() => setJustEmerged(false), 1400);
    return () => window.clearTimeout(timer);
  }, [proposalId, conditionMet, emerged]);

  return { emerged, justEmerged };
}
